/**
 * 일정 다듬기 — 지난 여행의 발자취에서 찾은 숫자로 이 일정을 손봅니다.
 *
 * 표본을 **먼저** 밝힙니다
 *   머리가 "지난 여행 3번의 발자취에서 찾았습니다" 인 이유입니다. 한 번
 *   가지고 단정하면 안 되고, 읽는 사람이 믿을지 말지 정할 근거가 표본
 *   크기입니다. 서버도 서로 다른 여행 2번 이상에서 본 것만 올립니다.
 *
 * 예정과 실제를 나란히 놓습니다
 *   `첨성대 60분 → 100분 (+40분)`. 숫자를 붙여 놓는 것만으로 설득이 되고,
 *   말로 풀면 오히려 흐려집니다.
 *
 * 초과만 붉게
 *   전부 색을 넣으면 고쳐야 할 것이 안 보입니다. 서버가 `over` 로
 *   표시해 준 것만 강조합니다.
 *
 * 제안에는 이유를 반드시 함께 둡니다
 *   이유 없는 제안은 안 따릅니다. 서버가 `reason` 을 항상 채웁니다.
 *
 * 꼬리에 약속을 둡니다
 *   "내 모임의 발자취만 씁니다" — 남의 위치를 끌어다 쓰지 않는다는 말을
 *   화면에 남겨야 합니다. 위치를 다루는 기능은 그 약속이 보여야 쓰입니다.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Section, SectionHeader } from 'components/ds';
import { IcClock, IcPin, IcHourglass, IcTaxi } from 'components/common/icons/EmojiIcons';
import { TraceInsights } from 'hooks/useTraceInsights';
import styles from './TraceInsightSection.module.css';

interface Props {
    insights: TraceInsights | null;
}

/* 제안 종류별 아이콘 — 셋이 같은 모양이면 무엇에 대한 말인지 훑어지지 않습니다.
   STAY_ADJUST 는 시간, PLACE_ADD 는 장소, MOVE_MODE 는 이동입니다. */
const SUGGESTION_ICON: Record<string, typeof IcPin> = {
    STAY_ADJUST: IcHourglass,
    PLACE_ADD: IcPin,
    MOVE_MODE: IcTaxi,
};

export const TraceInsightSection: React.FC<Props> = ({ insights }) => {
    const { t } = useTranslation();

    /* 표본이 없거나 찾은 게 없으면 섹션째 숨깁니다 — 빈 영역은 고장처럼
       보이고, 지난 여행이 없는 사람에게 할 말도 없습니다. */
    if (!insights || insights.sampleMeetingCount === 0) return null;
    if (insights.stays.length === 0 && insights.suggestions.length === 0) return null;

    return (
        <Section divided>
            <SectionHeader
                title={t('insight.title')}
                sub={t('insight.sample', { n: insights.sampleMeetingCount })}
            />

            {/* 예정 vs 실제 — 숫자를 나란히 */}
            {insights.stays.length > 0 && (
                <ul className={styles.list}>
                    {insights.stays.map((s) => (
                        <li key={`${s.dayNumber ?? 0}-${s.placeName}`} className={styles.row}>
                            <span className={styles.body}>
                                <span className={styles.place}>
                                    {s.dayNumber ? (
                                        <b className={styles.day}>{t('insight.day', { n: s.dayNumber })}</b>
                                    ) : null}
                                    {s.placeName}
                                </span>
                                <span className={styles.compare}>
                                    <IcClock size={11} />
                                    {t('insight.plannedVsActual', {
                                        planned: s.plannedMin, actual: s.actualMin,
                                    })}
                                    <em className={s.over ? styles.over : styles.diff}>
                                        {s.diffMin > 0 ? `+${s.diffMin}` : s.diffMin}
                                        {t('insight.min')}
                                    </em>
                                </span>
                            </span>
                            {/* 표본 크기를 줄마다 — 평균 하나로는 못 믿습니다 */}
                            <span className={styles.trips}>
                                {t('insight.trips', { n: s.tripCount })}
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            {/* 계획 시각이 없어 견줄 수 없던 장소 — 숨기지 않고 밝힙니다 */}
            {insights.skippedNoPlannedTime > 0 && (
                <p className={styles.note}>
                    {t('insight.skipped', { n: insights.skippedNoPlannedTime })}
                </p>
            )}

            {/* 제안 — 이유를 항상 붙여서 */}
            {insights.suggestions.length > 0 && (
                <ul className={styles.suggestions}>
                    {insights.suggestions.map((s, i) => {
                        const Ic = SUGGESTION_ICON[s.type] || IcPin;
                        return (
                        <li key={`${s.type}-${i}`} className={styles.suggestion}>
                            <Ic size={12} />
                            <span className={styles.suggestionBody}>
                                <span className={styles.suggestionTitle}>{s.title}</span>
                                <span className={styles.suggestionReason}>{s.reason}</span>
                            </span>
                        </li>
                        );
                    })}
                </ul>
            )}

            <p className={styles.promise}>{t('insight.promise')}</p>
        </Section>
    );
};
